import { type GitHubToolConfirmationRequest } from "../../session-logic";

function readRequestString(request: Record<string, unknown>, key: string): string | null {
  const value = request[key];
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function formatOwnerRepo(request: Record<string, unknown>): string | null {
  const owner = readRequestString(request, "owner");
  const name = readRequestString(request, "name");
  return owner && name ? `${owner}/${name}` : null;
}

export function formatGitHubOperationForDisplay(operation: string): string {
  if (operation === "link_project_repository") return "Link Project Repository";
  return operation
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ");
}

export function buildGitHubConfirmationRetryText(
  confirmation: GitHubToolConfirmationRequest,
): string {
  return [
    "The GitHub write action was approved by the user.",
    `Retry the dynamic tool \`${confirmation.toolName}\` with this approved confirmationId: \`${confirmation.confirmationId}\`.`,
    "Use the same request payload, plus the confirmationId field:",
    "```json",
    JSON.stringify(
      { ...confirmation.request, confirmationId: confirmation.confirmationId },
      null,
      2,
    ),
    "```",
  ].join("\n");
}

export function buildGitHubConfirmationDetailRows(
  confirmation: GitHubToolConfirmationRequest,
): Array<{ label: string; value: string }> {
  const request = confirmation.request;
  const repoFullName = readRequestString(request, "repoFullName") ?? formatOwnerRepo(request);
  const rows: Array<{ label: string; value: string | null }> = [
    { label: "Repository", value: repoFullName },
    { label: "Owner", value: readRequestString(request, "owner") },
    { label: "Visibility", value: readRequestString(request, "visibility") },
    { label: "Project path", value: readRequestString(request, "cwd") },
    { label: "Branch", value: readRequestString(request, "branch") },
    { label: "Base", value: readRequestString(request, "baseBranch") },
    { label: "Head", value: readRequestString(request, "headBranch") },
    { label: "Pull request", value: readRequestString(request, "title") },
    { label: "Workflow run", value: readRequestString(request, "runId") },
    { label: "Reset to", value: readRequestString(request, "ref") },
    { label: "Note", value: readRequestString(request, "message") },
  ];
  return rows.filter((row): row is { label: string; value: string } => Boolean(row.value));
}

export function getGitHubConfirmationRepoLabel(
  confirmation: GitHubToolConfirmationRequest,
): string {
  return (
    readRequestString(confirmation.request, "repoFullName") ??
    formatOwnerRepo(confirmation.request) ??
    readRequestString(confirmation.request, "cwd") ??
    confirmation.operation
  );
}

export function githubOperationRiskLabel(operation: string): string {
  switch (operation) {
    case "create_repository":
      return "Creates a GitHub repository in the connected account";
    case "link_project_repository":
      return "Changes this project's git remote";
    case "push_branch":
      return "Pushes commits to GitHub";
    case "create_pull_request":
      return "Opens a pull request on GitHub";
    case "rerun_workflow":
      return "Reruns a GitHub Actions workflow";
    case "stash":
      return "Sets your uncommitted changes aside (reversible via git stash pop)";
    case "commit":
      return "Commits your current changes to git";
    case "reset_hard":
      return "Permanently discards ALL uncommitted changes - this cannot be undone";
    default:
      return "Runs a GitHub write action";
  }
}
